import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import {
  getNextAvailableCertificateNumber,
  markCertificateNumberAsUsed,
  certificateNumberExists,
} from '@/utils/certificateUtils';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2 } from 'lucide-react';

const certificateFormSchema = z.object({
  userId: z.string().min(2, {
    message: "User ID must be at least 2 characters.",
  }),
  certificateType: z.string().min(2, {
    message: "Certificate Type must be selected.",
  }),
  courseId: z.string().min(2, {
    message: "Course must be selected.",
  }),
  templateId: z.string().min(2, {
    message: "Template must be selected.",
  }),
  certificateNumber: z.string().min(2, {
    message: "Certificate Number must be at least 2 characters.",
  }),
  issuedAt: z.date(),
  expiryDate: z.date().optional(),
  additionalText: z.string().optional(),
});

interface CreateCertificateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateCertificateDialog({
  open,
  onOpenChange,
}: CreateCertificateDialogProps) {
  const { toast } = useToast();
  const [isCreating, setIsCreating] = useState(false);

  const form = useForm<z.infer<typeof certificateFormSchema>>({
    resolver: zodResolver(certificateFormSchema),
    defaultValues: {
      userId: "",
      certificateType: "",
      courseId: "",
      templateId: "",
      certificateNumber: "",
      issuedAt: new Date(),
      expiryDate: undefined,
      additionalText: "",
    },
  });

  const handleSubmit = async (values: z.infer<typeof certificateFormSchema>) => {
    setIsCreating(true);
    try {
      // Check if the certificate number already exists
      const certificateExists = await certificateNumberExists(values.certificateNumber);
      if (certificateExists) {
        toast({
          title: "Error",
          description: "This certificate number already exists.",
          variant: "destructive",
        });
        return;
      }

      const { data, error } = await supabase
        .from('certificates')
        .insert({
          user_id: values.userId,
          certificate_type_id: values.certificateType,
          course_id: values.courseId,
          template_id: values.templateId,
          certificate_number: values.certificateNumber,
          issued_at: values.issuedAt.toISOString(),
          expiry_date: values.expiryDate?.toISOString() || null,
          additional_text: values.additionalText,
          unique_code: Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15), // Generate a unique code
        })
        .select()
        .single();

      if (error) {
        console.error("Error creating certificate:", error);
        toast({
          title: "Error",
          description: "Failed to create certificate.",
          variant: "destructive",
        });
        return;
      }

      // Mark the certificate number as used
      const markAsUsed = await markCertificateNumberAsUsed(values.certificateNumber, data.id);
      if (!markAsUsed) {
        toast({
          title: "Warning",
          description: "Certificate created, but failed to mark certificate number as used in inventory.",
          variant: "destructive",
        });
      }

      toast({
        title: "Success",
        description: "Certificate created successfully.",
      });
      form.reset();
      onOpenChange(false);
    } catch (error) {
      console.error("Error creating certificate:", error);
      toast({
        title: "Error",
        description: "Failed to create certificate.",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[625px]">
        <DialogHeader>
          <DialogTitle>Create Certificate</DialogTitle>
          <DialogDescription>
            Create a new certificate for a user.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="userId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>User ID</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter user ID" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="certificateType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Certificate Type</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a certificate type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="type1">Type 1</SelectItem>
                        <SelectItem value="type2">Type 2</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="courseId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Course</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter course ID" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="templateId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Template</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter template ID" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="certificateNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Certificate Number</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter certificate number" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="issuedAt"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Issued At</FormLabel>
                    <FormControl>
                      <Input
                        type="date"
                        value={field.value ? new Date(field.value).toISOString().split('T')[0] : ""}
                        onChange={(e) => field.onChange(new Date(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="expiryDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Expiry Date</FormLabel>
                    <FormControl>
                      <Input
                        type="date"
                        value={field.value ? new Date(field.value).toISOString().split('T')[0] : ""}
                        onChange={(e) => {
                          field.onChange(e.target.value ? new Date(e.target.value) : undefined);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="additionalText"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Additional Text</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter additional text" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isCreating}>
                {isCreating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  "Create"
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
